import Vue from 'vue';
import 'cvo-library';
import moment from 'moment';
import globalMixin from '@/commons/mixins/global';
import privateGlobal from '@/commons/mixins/private-global';
import { viewResources, ViewResource, Resource } from '@/commons/utils';
import getI18nConfig from '@/commons/config/i18n';
import { AppSetupConfig } from '@/commons/models';
import { setDefaultOptions } from 'date-fns';
import Loading from '@/commons/directives/loading/Loading';
import currencyInstance from '@/commons/modules/currency/currencyInstance';
import app from '@/commons/modules/app/currentApp';

const locales: {[lang:string]: () => Promise<any>} = {
  en: () => import(/* webpackChunkName: "date-fns-locale-en" */ 'date-fns/locale/en-US'),
  it: () => import(/* webpackChunkName: "date-fns-locale-it" */ 'date-fns/locale/it'),
  pl: () => import(/* webpackChunkName: "date-fns-locale-pl" */ 'date-fns/locale/pl'),
  ar: () => import(/* webpackChunkName: "date-fns-locale-ar" */ 'date-fns/locale/ar-SA'),
  hu: () => import(/* webpackChunkName: "date-fns-locale-hu" */ 'date-fns/locale/hu'),
  ro: () => import(/* webpackChunkName: "date-fns-locale-ro" */ 'date-fns/locale/ro'),
  es: () => import(/* webpackChunkName: "date-fns-locale-es" */ 'date-fns/locale/es'),
  kk: () => import(/* webpackChunkName: "date-fns-locale-kk" */ 'date-fns/locale/kk'),
  de: () => import(/* webpackChunkName: "date-fns-locale-de" */ 'date-fns/locale/de'),
  bg: () => import(/* webpackChunkName: "date-fns-locale-bg" */ 'date-fns/locale/bg'),
  el: () => import(/* webpackChunkName: "date-fns-locale-el" */ 'date-fns/locale/el'),
}

export default async function appSetup(setupConfig: AppSetupConfig | ((configs: any) => AppSetupConfig)) {
  Object.entries(viewDto).forEach(([name, item]) => {
    const contents = item.contents ?? null;
    const options = item.options ?? null;

    viewResources.add(name).setContents(contents).setOptions(options);
  });

  viewResources.addGeneric('url').setValues(urlsDto);
  viewResources.addGeneric('userInfo').setValues(userInfoDto);
  viewResources.addGeneric('contextInfo').setValues(contextDto);
  viewResources.addGeneric('formItems').setValues(formItemsDto);
  viewResources.addGeneric('subscription').setValues(subscriptionDto);

  try {
    const resourceOptions = viewResources
      .get<ViewResource>('account')
      ?.getOptions();

    if (resourceOptions?.paymentUrls) {
      viewResources.get<Resource>('url').setValues(resourceOptions.paymentUrls);
    }
  } catch (err) {
    console.error('Error initializing payment urls', err);
  }

  const config = typeof setupConfig === "function" ? setupConfig(contextDto.product) : setupConfig;

  const {
    components = {},
    mixins = [],
    views = {},
  } = config;

  if (
    contextDto.analyticsEnabled &&
    Array.isArray(contextDto.gtmIds) &&
    contextDto.gtmIds.length
  ) {
    const { default: VueGtm } = await import(
      /* webpackChunkName: "gtm-analytics" */ '@gtm-support/vue2-gtm'
    );

    Vue.use(VueGtm, {
      id: contextDto.gtmIds,
      queryParams: {
        gtm_auth: contextDto.gtmAuth,
        gtm_preview: contextDto.gtmPreview,
        gtm_cookies_win: contextDto.gtmCookiesWin,
      },
      debug: false,
      loadScript: true,
      // vueRouter: router, // Pass the router instance to automatically sync with router
    });
  }

  Vue.directive('loading', Loading);

  // Unused legacy property, remove to prevent flooding the console with Vue warnings
  // @ts-ignore
  delete globalMixin.propsData;

  const { activeCurrency, currentLanguage } = viewDto.language.options;

  moment.locale(currentLanguage);

  const options = { tenant: contextDto.tenantCarrier, currency: activeCurrency, language: currentLanguage };
  currencyInstance.setConfig(options);

  app.withViews(views);
  await app.initialize();

  getI18nConfig(currentLanguage)
    .then(async (i18n) => {
      const currentLang = String(i18n.locale).split('-')[0];
      const currentLocale = locales[currentLang] ? (await locales[currentLang]()).default : (await locales['en']()).default;
      setDefaultOptions({ locale: currentLocale });

      new Vue({
        el: '#app',
        components: { ...components },
        mixins: [globalMixin, privateGlobal, ...mixins],
        i18n,
      });
    });
}
